<!-- Empty / baseline state: symptom module not completed for this scan -->
<div *ngIf="!vm.available" class="card" style="margin-bottom:12px;padding:20px 18px;text-align:center;">
  <div style="font-size:15px;font-weight:800;color:var(--navy);margin-bottom:8px;">{{ 'results.sym.empty.title' | translate }}</div>
  <p style="font-size:12.5px;color:var(--g500);line-height:1.6;margin:0;">{{ (webLoop ? 'results.sym.empty.bodyWeb' : 'results.sym.empty.body') | translate }}</p>
</div>

<ng-container *ngIf="vm.available">
  <!-- HERO -->
  <div class="card hi-hero-card" style="margin-bottom:12px;padding:16px 18px 14px;">
    <div style="display:flex;align-items:baseline;gap:7px;margin-bottom:4px;">
      <div style="font-size:15px;font-weight:800;color:var(--navy);letter-spacing:-0.2px;">{{ 'results.sym.title' | translate }}</div>
    </div>
    <div style="font-size:10px;color:var(--g400);margin-bottom:10px;">{{ 'results.sym.subtitle' | translate }}</div>

    <!-- wordfirst sec 2: evaluation WORD + 3-level bar; the burden number moves
         into the reveal at the bottom of the hero. -->
    <app-band-scale [band]="vm.band" [config]="bandConfig" [score]="vm.score" [aboutKey]="'results.about.symptoms'"></app-band-scale>

    <div class="hi-hero-copy" style="margin-top:13px;margin-bottom:10px;">
      <p style="font-size:13px;font-weight:700;color:var(--navy);line-height:1.45;margin:0;">{{ vm.copy1Key | translate }}</p>
      <p style="font-size:12px;color:var(--g600);line-height:1.65;margin:0;">{{ 'results.sym.copy2' | translate }}</p>
    </div>

    <div style="background:var(--g50);border:1px solid var(--g100);border-radius:10px;padding:9px 12px;margin-bottom:10px;">
      <div style="font-size:11px;color:var(--g600);line-height:1.55;">{{ 'results.sym.reported' | translate: { count: vm.reportedCount } }}</div>
    </div>

    <div style="display:flex;gap:7px;margin-bottom:12px;">
      <div style="flex:1;background:#fdeef6;border-radius:9px;padding:8px 6px;text-align:center;">
        <div style="font-size:20px;font-weight:800;color:#c44a00;line-height:1;">{{ vm.buckets.pronounced }}</div>
        <div style="font-size:10px;color:#c44a00;font-weight:600;margin-top:2px;">{{ 'results.sym.buckets.pronounced' | translate }}</div>
      </div>
      <div style="flex:1;background:#fff8ed;border-radius:9px;padding:8px 6px;text-align:center;">
        <div style="font-size:20px;font-weight:800;color:#b37400;line-height:1;">{{ vm.buckets.moderate }}</div>
        <div style="font-size:10px;color:#b37400;font-weight:600;margin-top:2px;">{{ 'results.sym.buckets.moderate' | translate }}</div>
      </div>
      <div style="flex:1;background:#edf8f3;border-radius:9px;padding:8px 6px;text-align:center;">
        <div style="font-size:20px;font-weight:800;color:#1a7a4a;line-height:1;">{{ vm.buckets.mild }}</div>
        <div style="font-size:10px;color:#1a7a4a;font-weight:600;margin-top:2px;">{{ 'results.sym.buckets.mild' | translate }}</div>
      </div>
    </div>

    <details class="reveal" *ngIf="vm.score !== null">
      <summary>
        <svg class="rev-chev" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6"><polyline points="6 9 12 15 18 9"/></svg>
        {{ 'results.reveal.numbersBehind' | translate }}
      </summary>
      <div class="rev-body">
        <div class="numrow">
          <div class="bignum">{{ vm.score }}<span>/100</span></div>
          <div style="flex:1;min-width:0;">
            <span class="dir-chip">{{ 'results.sym.lowerBetter' | translate }}</span>
            <p>{{ 'results.reveal.scoreNote' | translate }}</p>
          </div>
        </div>
      </div>
    </details>

  </div>

  <!-- PROGRESS OVER TIME (#22) -->
  <app-progress-card [vm]="progress" (goPaywall)="goPaywall.emit()"></app-progress-card>

  <!-- DEEP DIVE: full symptom breakdown (domain scores + top symptoms) -->
  <details class="ia-deep" *ngIf="vm.domains.length || vm.topSymptoms.length">
    <summary class="ia-deep-toggle">
      <div>
        <div>{{ 'results.sym.deepTitle' | translate }}</div>
        <div style="font-size:11px;font-weight:500;color:var(--g400);margin-top:2px;">{{ 'results.sym.deepSub' | translate }}</div>
      </div>
      <svg class="ia-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M6 9l6 6 6-6"/></svg>
    </summary>

    <!-- Domain scores -->
    <div class="card skin-score-card" *ngIf="vm.domains.length" style="margin-top:10px;margin-bottom:10px;">
      <h3 class="section-title">{{ 'results.sym.domainsTitle' | translate }}</h3>
      <p style="font-size:11px;color:var(--g400);line-height:1.55;margin:0 0 8px;">{{ 'results.sym.domainsSub' | translate }}</p>
      <div class="ia-score-row" *ngFor="let d of vm.domains">
        <div style="flex:1;min-width:0;"><div style="font-size:13px;font-weight:600;color:var(--navy);">{{ d.labelKey | translate }}</div></div>
        <div class="ia-bar-track"><div class="ia-bar-fill" [attr.data-score]="d.value" [style.width.%]="d.value * 10" [style.background]="d.color"></div></div>
        <div class="ia-average-value" *ngIf="d.avg != null">{{ 'results.sym.avg' | translate }}<strong>{{ d.avg }}</strong></div>
      </div>
    </div>

    <!-- Top symptoms -->
    <div class="card skin-score-card" *ngIf="vm.topSymptoms.length" style="margin-bottom:10px;">
      <h3 class="section-title">{{ 'results.sym.topTitle' | translate }}</h3>
      <p style="font-size:11px;color:var(--g400);line-height:1.55;margin:0 0 8px;">{{ 'results.sym.topSub' | translate }}</p>
      <div class="ia-score-row" *ngFor="let s of vm.topSymptoms">
        <div style="flex:1;min-width:0;"><div style="font-size:13px;font-weight:600;color:var(--navy);">{{ s.labelKey | translate }}</div></div>
        <div class="ia-bar-track"><div class="ia-bar-fill" [attr.data-score]="s.value" [style.width.%]="s.value * 10" [style.background]="s.color"></div></div>
      </div>
    </div>

    <!-- Cluster map: how the reported symptoms connect -->
    <app-symptom-cluster-map *ngIf="vm.clusters.length" [clusters]="vm.clusters"></app-symptom-cluster-map>
  </details>

  <!-- TOOLS WE OFFER (rows driven by the user's actual symptom clusters) -->
  <app-tools-recommendation domain="symptoms" [activeCategories]="vm.toolCategoryKeys"></app-tools-recommendation>
</ng-container>
